import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageSquare, MessagesSquare, Check, Info, Minus, Plus } from 'lucide-react';
import { Breadcrumb, CtaBand } from './DemoSiteLayout';
import { T, SURFACE, HAIRLINE, SHADOW, Hero, DemoLink, Band } from './siteUI';

/* =========================================================
   Pricing.

   Plan rates and the comparison table are the single source of truth in
   PLANS / RATE_ROWS below — the cards, the estimator, and the table all read
   from them, so a rate change lands in one place.
   ========================================================= */

/* `commitment` and `voicePerMin` drive the estimator. Enterprise is quoted, so
   both are null and it renders as "Contact sales" rather than a number. */
const PLANS = [
  {
    id: 'payg',
    name: 'Pay As You Go',
    price: '$0',
    unit: '/mo',
    note: 'Start with $25',
    commitment: 0,
    voicePerMin: 0.1,
    rates: [
      { label: 'Voice', value: '$0.10/min', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
      { label: 'SMS', value: '$0.03/msg', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
      { label: 'AI Chat', value: '$0.01/msg', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
    ],
    features: ['1 AI agent', 'No monthly commitment', 'Balance never expires'],
    cta: 'Start with $25',
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$99',
    unit: '/mo',
    note: 'Includes $99 usage',
    commitment: 99,
    voicePerMin: 0.08,
    rates: [
      { label: 'Voice', value: '$0.08/min', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
      { label: 'SMS', value: '$0.025/msg', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
      { label: 'AI Chat', value: '$0.008/msg', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
    ],
    features: ['Up to 3 AI agents', 'Standard integrations', 'Analytics'],
    cta: 'Choose Starter',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$249',
    unit: '/mo',
    note: 'Includes $249 usage',
    commitment: 249,
    voicePerMin: 0.07,
    popular: true,
    rates: [
      { label: 'Voice', value: '$0.07/min', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
      { label: 'SMS', value: '$0.022/msg', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
      { label: 'AI Chat', value: '$0.006/msg', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
    ],
    features: ['Up to 10 AI agents', 'API & webhooks', 'Advanced workflows'],
    cta: 'Choose Growth',
  },
  {
    id: 'business',
    name: 'Business',
    price: '$499',
    unit: '/mo',
    note: 'Includes $499 usage',
    commitment: 499,
    voicePerMin: 0.06,
    rates: [
      { label: 'Voice', value: '$0.06/min', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
      { label: 'SMS', value: '$0.02/msg', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
      { label: 'AI Chat', value: '$0.005/msg', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
    ],
    features: ['Unlimited AI agents', 'Advanced routing', 'Business support'],
    cta: 'Choose Business',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    unit: '',
    note: 'Volume pricing',
    commitment: null,
    voicePerMin: null,
    rates: [
      { label: 'Voice', value: 'from $0.05/min', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
      { label: 'SMS', value: 'Volume pricing', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
      { label: 'AI Chat', value: 'from $0.004/msg', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
    ],
    features: ['SSO & SLA', 'High concurrency', 'Dedicated support'],
    cta: 'Contact sales',
  },
];

const RATE_ROWS = [
  { label: 'Monthly commitment', values: ['$0', '$99', '$249', '$499', 'Custom'] },
  { label: 'Included monthly usage', values: ['—', '$99', '$249', '$499', 'Custom'] },
  { label: 'Voice', values: ['$0.10/min', '$0.08/min', '$0.07/min', '$0.06/min', 'From $0.05/min'], emphasis: true },
  { label: 'Enhanced Accuracy / Dual STT', values: ['+$0.01/min', '+$0.01/min', '+$0.01/min', '+$0.01/min', '+$0.01/min'] },
  { label: 'SMS', values: ['$0.030/msg', '$0.025/msg', '$0.022/msg', '$0.020/msg', 'Volume'] },
  { label: 'AI Chat', values: ['$0.010/msg', '$0.008/msg', '$0.006/msg', '$0.005/msg', 'From $0.004/msg'] },
  { label: 'AI Agents', values: ['1', '3', '10', 'Unlimited', 'Unlimited'] },
];

const FAQS = [
  {
    q: 'Do I pay a platform fee plus usage?',
    a: 'No. FreshRoad pricing is usage-based. Monthly plans include usage that helps lower your effective rates as you grow.',
  },
  {
    q: 'What happens if I exceed my monthly commitment?',
    a: 'If you exceed your included usage, overages are billed at your plan rate.',
  },
  {
    q: 'Can I choose a different voice provider?',
    a: 'Yes. FreshRoad Voice is included in the listed rates. You can connect another supported provider, and its charges are billed at that provider’s rates on top of your plan.',
  },
  {
    q: 'Does Pay As You Go balance expire?',
    a: 'No. Your Pay As You Go balance carries over month to month and never expires.',
  },
];

/* ---------- estimator ----------
   You are billed the greater of your monthly commitment and your metered
   usage — the commitment already includes that much usage. Multiplying
   minutes by the rate alone understates the bill on every plan whose
   commitment is above the usage figure. */
const estimateMonthly = (plan, minutes) => {
  if (plan.voicePerMin == null) return null;
  const usage = minutes * plan.voicePerMin;
  return { usage, total: Math.max(plan.commitment, usage), atFloor: plan.commitment > usage };
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;

/* ---------- plan card ---------- */

const PlanCard = ({ plan }) => (
  <div
    className={`relative flex flex-col p-6 ${
      plan.popular
        ? `rounded-2xl bg-white ring-2 ring-teal-500/70 ${SHADOW.raised}`
        : SURFACE.card
    }`}
  >
    {plan.popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-teal-600 px-3 py-1 text-[11px] font-bold text-white">
        Most popular
      </span>
    )}

    <p className="text-center text-[14.5px] font-semibold">{plan.name}</p>
    <p className="mt-3 text-center">
      <span className="text-[38px] font-bold leading-none tracking-[-0.03em]">{plan.price}</span>
      {plan.unit && <span className="text-[14px] font-medium text-slate-500">{plan.unit}</span>}
    </p>
    <p className={`mt-2 text-center ${T.micro}`}>{plan.note}</p>

    <div className={`mt-5 space-y-2.5 border-t border-slate-100 pt-5`}>
      {plan.rates.map(({ label, value, icon: Icon, tint }) => (
        <div key={label} className="flex items-center gap-2.5">
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${tint}`}>
            <Icon size={13} strokeWidth={2} />
          </span>
          <span className="flex-1 text-[12.5px] font-medium text-slate-600">{label}</span>
          <span className="text-[12.5px] font-semibold">{value}</span>
        </div>
      ))}
    </div>

    <ul className="mt-5 space-y-2.5 border-t border-slate-100 pt-5">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-2.5 text-[12.5px] text-slate-600">
          <Check size={14} strokeWidth={2.8} className="mt-0.5 shrink-0 text-teal-600" />
          {f}
        </li>
      ))}
    </ul>

    <div className="mt-6 flex-1" />
    <DemoLink
      className={
        plan.popular
          ? 'w-full !px-4 !py-2.5 !text-[13.5px]'
          : 'w-full !bg-white !text-teal-700 hover:!bg-teal-50 !shadow-none ring-1 ring-slate-900/5 !px-4 !py-2.5 !text-[13.5px]'
      }
    >
      {plan.cta}
    </DemoLink>
  </div>
);

/* ---------- estimator ---------- */

const Estimator = () => {
  const [minutes, setMinutes] = useState(2500);
  const results = useMemo(() => PLANS.map((p) => ({ plan: p, est: estimateMonthly(p, minutes) })), [minutes]);

  return (
    <section className={`px-6 py-8 md:px-10 ${SURFACE.band}`}>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <div>
          <h2 className={T.h3}>Estimate your monthly cost</h2>
          <label htmlFor="voice-minutes" className="mt-5 block text-[12.5px] font-semibold text-slate-600">
            Monthly voice minutes
          </label>
          <input
            id="voice-minutes"
            type="range"
            min={0}
            max={10000}
            step={100}
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-teal-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-600"
          />
          <div className={`mt-2 flex justify-between ${T.micro}`}>
            <span>0</span>
            <span>10,000</span>
          </div>
          <p className="mt-5 text-center text-[30px] font-bold tracking-[-0.02em] lg:text-left">
            {minutes.toLocaleString()}
          </p>
          <p className={`text-center lg:text-left ${T.micro}`}>minutes / month</p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {results.map(({ plan, est }) => (
            <div
              key={plan.id}
              className={`flex flex-col items-center justify-center px-2 py-4 text-center ${
                plan.popular ? 'rounded-2xl bg-teal-50/60 ring-2 ring-teal-500/60' : `rounded-2xl bg-slate-50/60 ${HAIRLINE}`
              }`}
            >
              <p className="text-[12px] font-semibold text-slate-500">{plan.name}</p>
              {est ? (
                <>
                  <p className="mt-2 text-[24px] font-bold tracking-[-0.02em]">{money(est.total)}</p>
                  <p className={T.micro}>est. / month</p>
                  {/* When the commitment is higher than metered usage, the
                      commitment is what you actually pay — say so instead of
                      quoting the lower usage figure. */}
                  {est.atFloor && (
                    <p className="mt-1 text-[10.5px] leading-tight text-slate-400">
                      plan minimum
                      <br />
                      {money(est.usage)} usage
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="mt-2 text-[18px] font-bold leading-tight tracking-[-0.02em]">Contact sales</p>
                  <p className={T.micro}>volume pricing</p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <p className={`mx-auto mt-7 max-w-[70ch] text-center ${T.micro}`}>
        Estimates cover standard FreshRoad Voice only. You are billed the greater of your monthly
        commitment or your metered usage. Optional voice providers, Enhanced Accuracy / Dual STT,
        international telephony, SMS, and AI Chat are charged separately.
      </p>
    </section>
  );
};

/* ---------- rate table ---------- */

const RateTable = () => (
  <Band title="Plans & usage rates" subtitle="Every rate, side by side. Volume discounts apply as you grow.">
    {/* Wide table: scrolls inside its own container so the page never scrolls sideways. */}
    <div className="mt-8 -mx-2 overflow-x-auto px-2">
      <table className="w-full min-w-[760px] border-collapse text-left">
        <caption className="sr-only">Monthly commitment and per-unit rates for each FreshRoad plan</caption>
        <thead>
          <tr>
            <th scope="col" className="w-[210px] rounded-l-xl bg-slate-800 px-4 py-3.5">
              <span className="sr-only">Plan feature</span>
            </th>
            {PLANS.map((p, i) => (
              <th
                key={p.id}
                scope="col"
                className={`bg-teal-600 px-4 py-3.5 text-center text-[13px] font-bold text-white ${
                  i === PLANS.length - 1 ? 'rounded-r-xl' : ''
                }`}
              >
                {p.name === 'Pay As You Go' ? 'PAYG' : p.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RATE_ROWS.map((row, r) => (
            <tr key={row.label} className={r % 2 ? 'bg-slate-50/70' : ''}>
              <th
                scope="row"
                className="border-b border-slate-100 px-4 py-3.5 text-[13px] font-semibold text-slate-800"
              >
                {row.label}
              </th>
              {row.values.map((v, i) => (
                <td
                  key={`${row.label}-${PLANS[i].id}`}
                  className={`border-b border-slate-100 px-4 py-3.5 text-center text-[13px] ${
                    row.emphasis ? 'font-bold text-slate-900' : 'text-slate-600'
                  }`}
                >
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Band>
);

/* ---------- FAQ ---------- */

const FaqItem = ({ item, open, onToggle, id }) => (
  <div className={`overflow-hidden ${SURFACE.card}`}>
    <h3>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={`${id}-button`}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-teal-600"
      >
        <span className="text-[14px] font-semibold">{item.q}</span>
        <span className="shrink-0 text-teal-600">
          {open ? <Minus size={17} /> : <Plus size={17} />}
        </span>
      </button>
    </h3>
    {open && (
      <div id={`${id}-panel`} role="region" aria-labelledby={`${id}-button`} className="px-5 pb-4">
        <p className={`max-w-[76ch] ${T.body}`}>{item.a}</p>
      </div>
    )}
  </div>
);

const Faq = () => {
  const [open, setOpen] = useState(() => new Set([0, 1]));
  const toggle = (i) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <section className="px-2 py-8">
      <h2 className={`text-center ${T.h2}`}>Frequently asked questions</h2>
      <div className="mx-auto mt-8 max-w-[820px] space-y-3">
        {FAQS.map((item, i) => (
          <FaqItem
            key={item.q}
            id={`faq-${i}`}
            item={item}
            open={open.has(i)}
            onToggle={() => toggle(i)}
          />
        ))}
      </div>
    </section>
  );
};

/* ---------- page ---------- */

const DemoSitePricing = () => (
  <>
    <Breadcrumb trail={['Home', 'Products', 'Conversational AI', 'Pricing']} />

    <section className="py-8">
      <Hero
        eyebrow="Conversational AI pricing"
        title={
          <>
            Simple pricing for every <span className="text-teal-600">customer conversation.</span>
          </>
        }
        actions={
          <>
            <DemoLink />
            {/* Styled as a ghost button but rendered as a link — a <button>
                wrapping an <a> is invalid and breaks keyboard semantics. */}
            <Link
              to="/site/customer-ai-platform"
              className={`inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-[14px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 ${HAIRLINE} ${SHADOW.card} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}
            >
              See what&rsquo;s included
            </Link>
          </>
        }
      >
        Start with a monthly subscription, or choose a plan that fits your volume. Support more
        conversations and drive better outcomes at every stage.
      </Hero>
    </section>

    {/* Plans */}
    <section className="grid grid-cols-1 gap-5 pt-3 sm:grid-cols-2 lg:grid-cols-5">
      {PLANS.map((p) => (
        <PlanCard key={p.id} plan={p} />
      ))}
    </section>

    <div className={`flex items-start gap-3 px-6 py-4 ${SURFACE.band}`}>
      <Info size={16} className="mt-0.5 shrink-0 text-teal-600" />
      <p className={`max-w-[92ch] ${T.body}`}>
        These rates cover Conversational AI. Teammate and Orbiter are priced separately — see their
        product pages. FreshRoad Voice is included in the listed voice rates; other supported voice
        providers may add charges. Enhanced Accuracy / Dual STT is an optional add-on at +$0.01/min.
      </p>
    </div>

    <RateTable />

    <Estimator />

    <Faq />

    <CtaBand
      title="Ready to transform customer conversations?"
      subtitle="Book a demo and see the platform in action."
      primary="Book a demo"
    />
  </>
);

export default DemoSitePricing;
